import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Card, 
  Typography, 
  TextField, 
  Button, 
  Link,
  IconButton,
  InputAdornment,
  alpha,
  Container,
  Alert,
  CircularProgress,
  useTheme
} from '@mui/material';
import { Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { login, clearError } from '../redux/slices/authSlice';

const SignInView = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoading, error, isAuthenticated } = useSelector((state) => state.auth);

  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value.trimStart()
    });
    if (error) dispatch(clearError());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(login({
      ...formData,
      email: formData.email.trim(),
      password: formData.password.trim(),
    }));
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      width: '100%',
      display: 'flex', 
      flexDirection: 'column',
      pt: 'env(safe-area-inset-top, 0px)',
      pb: 'env(safe-area-inset-bottom, 0px)',
      background: theme.palette.mode === 'dark'
        ? `linear-gradient(135deg, ${alpha(theme.palette.background.default, 0.95)} 0%, ${alpha(theme.palette.background.neutral, 0.95)} 100%)`
        : 'linear-gradient(135deg, rgba(235, 244, 255, 0.4) 0%, rgba(255, 235, 244, 0.4) 100%), #F9FAFB',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Decorative Blur Orbs */}
      <Box sx={{ position: 'absolute', top: -100, left: -100, width: 400, height: 400, borderRadius: '50%', background: (theme) => alpha(theme.palette.primary.main, 0.08), filter: 'blur(80px)', zIndex: 0 }} />
      <Box sx={{ position: 'absolute', bottom: -100, right: -100, width: 400, height: 400, borderRadius: '50%', background: (theme) => alpha(theme.palette.secondary.main, 0.08), filter: 'blur(80px)', zIndex: 0 }} />

      {/* Top Header Section */}
      <Box sx={{ p: { xs: 2, sm: 3 }, display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 1 }}>
        <Box sx={{ cursor: 'pointer' }}>
           <img src="/logo.webp" alt="Reseller Bazzar Logo" style={{ height: 48 }} />
        </Box>
        <Link href="mailto:support@resellerbazzar.in?subject=Admin%20Login%20Help" sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' }, fontWeight: 700, fontSize: '0.875rem', textDecoration: 'none' }}>
           Need help?
        </Link>
      </Box>

      {/* Main Content */}
      <Container maxWidth="sm" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, py: { xs: 2, sm: 4 }, pb: { xs: 4, sm: 8 }, px: { xs: 2, sm: 3 } }}>
        <Card sx={{ 
          width: '100%', 
          maxWidth: 420, 
          p: { xs: 2.75, sm: 4, md: 5 }, 
          borderRadius: 3,
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 12px 32px 0 rgba(0, 0, 0, 0.4)'
            : '0 12px 32px 0 rgba(145, 158, 171, 0.12)',
        }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.5, letterSpacing: -0.5 }}>Sign in to Admin</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Enter your administrative credentials to continue
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Email Field */}
            <Box>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 700, color: 'text.secondary' }}>
                Email address
              </Typography>
              <TextField 
                name="email"
                fullWidth 
                value={formData.email}
                onChange={handleChange}
                placeholder="admin@resellerbazzar.in" 
                variant="outlined" 
                required
              />
            </Box>

            {/* Password Field */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                  Password
                </Typography>
                <Link href="mailto:support@resellerbazzar.in?subject=Admin%20Password%20Reset" sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'primary.main', textDecoration: 'none' }}>
                  Forgot password?
                </Link>
              </Box>
              <TextField 
                name="password"
                fullWidth 
                type={showPassword ? 'text' : 'password'}
                placeholder="********"
                value={formData.password}
                onChange={handleChange}
                variant="outlined" 
                required
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton aria-label="Toggle password visibility" onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ color: 'text.disabled' }}>
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>

            <Button 
              fullWidth 
              type="submit"
              variant="contained" 
              size="large"
              disabled={isLoading}
              sx={{ 
                py: 1.5, 
                fontWeight: 700, 
                borderRadius: 2,
                textTransform: 'none',
                boxShadow: theme.shadows[1],
                '&:hover': {
                  boxShadow: theme.shadows[2],
                }
              }}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Sign in'}
            </Button>
          </Box>
        </Card>
      </Container>
    </Box>
  );
};

export default SignInView;
